import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  Container,
  List,
  Typography,
  Button,
  LinearProgress,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  IconButton,
  Collapse,
  useScrollTrigger,
} from '@mui/material';
import { api } from '../services/api';
import ChecklistItem from '../components/ChecklistItem';
import ChecklistItemModal from '../components/ChecklistItemModal';
import styles from '../styles/Checklist.module.css';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

const MemoizedChecklistItem = memo(ChecklistItem);

function Checklist({ isActive }) {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [filterSectionOpen, setFilterSectionOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollTrigger = useScrollTrigger({
    threshold: 50,
    disableHysteresis: true,
  });
  const [filteredItems, setFilteredItems] = useState([]);
  const [showCategory, setShowCategory] = useState(false);

  const loadItems = async () => {
    try {
      const response = await api.getAllItems();
      setItems(response.data);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  useEffect(() => {
    if (isActive) {
      setLoading(true);
      loadItems().finally(() => {
        setLoading(false);
      });
    }
  }, [isActive]);

  const handleScroll = useCallback((e) => {
    const currentScrollY = e.target.scrollTop;

    if (currentScrollY < lastScrollY.current) {
      setIsVisible(true);
    } else if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
      setIsVisible(false);
    }

    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    const container = document.querySelector(`.${styles.content}`);
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const months = {};
    items.forEach((item) => {
      const monthYear = new Date(item.dueBy).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      });
      months[monthYear] = true; // 默认展开
    });
    setExpandedMonths(months);
  }, [items]);

  useEffect(() => {
    const newFilteredItems = items.filter((item) => {
      const matchesSearch = item.taskItemName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        !selectedCategory || item.category === selectedCategory;
      const matchesCompleted = showCompleted || !item.isDone;
      return matchesSearch && matchesCategory && matchesCompleted;
    });
    setFilteredItems(newFilteredItems);
  }, [items, searchTerm, selectedCategory, showCompleted]);

  const handleToggle = async (item) => {
    try {
      await api.updateItem(item._id, { ...item, isDone: !item.isDone });
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem._id === item._id
            ? { ...prevItem, isDone: !prevItem.isDone }
            : prevItem
        )
      );
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleSave = async (editedItem) => {
    try {
      if (editedItem._id) {
        await api.updateItem(editedItem._id, editedItem);
      } else {
        await api.createItem(editedItem);
      }
      setModalOpen(false);
      loadItems();
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await api.deleteItem(itemId);
      setItems((prevItems) => prevItems.filter((item) => item._id !== itemId));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleReset = async () => {
    try {
      await api.resetChecklist();
      loadItems();
    } catch (error) {
      console.error('Error resetting checklist:', error);
    }
  };

  const categories = [
    'Venue',
    'Wedding website',
    'Photos & videos',
    'Food & drink',
    'Attire',
    'Music',
    'Flowers & decor',
    'Registry',
    'Invitations & paper',
    'Beauty',
    'Ceremony',
    'Guests',
    'Travel',
    'Details',
  ];

  const groupItemsByMonth = (items) => {
    const grouped = {};

    items.forEach((item) => {
      const date = new Date(item.dueBy);
      const monthYear = date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
      }); // 使用月份和年份作为键
      if (!grouped[monthYear]) {
        grouped[monthYear] = {
          title: monthYear,
          items: [],
        };
      }
      grouped[monthYear].items.push(item);
    });

    // 按月份正确排序
    return Object.values(grouped)
      .sort((a, b) => new Date(a.title) - new Date(b.title)) // 按月份排序
      .map(({ title, items }) => ({ title, items }));
  };

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleAddTask = () => {
    const newItem = {
      taskItemName: '',
      taskItemTip: '',
      category: 'Details',
      dueBy: new Date(),
      isDone: false,
    };
    setSelectedItem(newItem);
    setModalOpen(true);
  };

  const handleMonthToggle = (monthYear) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [monthYear]: !prev[monthYear],
    }));
  };

  return (
    <Container maxWidth="md" className={styles.container}>
      <Box
        className={`${styles.header} 
          ${!isVisible ? styles.headerHidden : ''}`}
      >
        <Box className={styles.headerTop}>
          <Typography
            variant="body1"
            component="h1"
            className={`${styles.title} ${
              scrollTrigger ? styles.titleScrolled : ''
            }`}
          >
            Your Wedding Checklist
          </Typography>

          <IconButton
            color="primary"
            onClick={handleAddTask}
            className={`${styles.addButton} ${
              scrollTrigger ? styles.addButtonScrolled : ''
            }`}
            size="medium"
          >
            <AddIcon />
          </IconButton>
        </Box>

        <Box className={styles.progressSection}>
          {!scrollTrigger && (
            <Box className={styles.progressContainer}>
              <Typography variant="body1" className={styles.progressText}>
                {`${Math.round(
                  (items.filter((item) => item.isDone).length / items.length) *
                    100
                )}% Completed`}
              </Typography>
            </Box>
          )}

          <Box className={styles.progressWrapper}>
            <LinearProgress
              variant="determinate"
              value={
                (items.filter((item) => item.isDone).length / items.length) *
                100
              }
              className={`${styles.linearProgress} ${
                scrollTrigger ? styles.progressScrolled : ''
              }`}
            />
            {scrollTrigger && (
              <Typography variant="body2" className={styles.progressTextSmall}>
                {`${Math.round(
                  (items.filter((item) => item.isDone).length / items.length) *
                    100
                )}%`}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box className={styles.content}>
        <Box className={styles.scrollContent}>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Grid container spacing={2} className={styles.gridContainer}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search tasks"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    endAdornment: searchTerm && (
                      <IconButton
                        onClick={() => setSearchTerm('')}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Filter by Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    className={styles.switchLabel}
                    control={
                      <Switch
                        checked={showCompleted}
                        onChange={(e) => setShowCompleted(e.target.checked)}
                        color="primary"
                        className={styles.switch}
                      />
                    }
                    label="Show completed"
                  />
                  <FormControlLabel
                    className={styles.switchLabel}
                    control={
                      <Switch
                        checked={showCategory}
                        onChange={(e) => setShowCategory(e.target.checked)}
                        color="primary"
                        className={styles.switch}
                      />
                    }
                    label="Show category"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* 移动端显示的折叠面板 */}
          <Box
            sx={{ display: { xs: 'block', md: 'none' } }}
            className={styles.filterSection}
          >
            <Box
              onClick={() => setFilterSectionOpen(!filterSectionOpen)}
              className={styles.filterHeader}
            >
              <Typography variant="h6">Search & filter</Typography>
              <IconButton
                className={`${styles.caretIcon} ${
                  filterSectionOpen ? styles.caretOpen : ''
                }`}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </Box>
            <Collapse in={filterSectionOpen}>
              <Grid container spacing={2} className={styles.gridContainer}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Search tasks"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <IconButton
                          onClick={() => setSearchTerm('')}
                          size="small"
                        >
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      label="Filter by Category"
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <FormControlLabel
                      className={styles.switchLabel}
                      control={
                        <Switch
                          checked={showCompleted}
                          onChange={(e) => setShowCompleted(e.target.checked)}
                          color="primary"
                          className={styles.switch}
                        />
                      }
                      label="Show completed"
                    />
                    <FormControlLabel
                      className={styles.switchLabel}
                      control={
                        <Switch
                          checked={showCategory}
                          onChange={(e) => setShowCategory(e.target.checked)}
                          color="primary"
                          className={styles.switch}
                        />
                      }
                      label="Show category"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          <div className={styles.listContainer}>
            <div
              className={`${styles.loadingOverlay} ${
                loading ? styles.visible : ''
              }`}
            >
              <div className={styles.spinner} />
            </div>

            {groupItemsByMonth(filteredItems).map((group) => (
              <Box key={group.title} sx={{ mb: 4 }}>
                <Box
                  onClick={() => handleMonthToggle(group.title)}
                  className={styles.monthHeader}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="h6" className={styles.monthTitle}>
                    {group.title}
                  </Typography>
                  <IconButton
                    className={`${styles.caretIcon} ${
                      expandedMonths[group.title] ? styles.caretOpen : ''
                    }`}
                    size="small"
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                </Box>
                <Collapse in={expandedMonths[group.title]}>
                  <List sx={{ padding: 0 }}>
                    {group.items.map((item) => (
                      <MemoizedChecklistItem
                        key={item._id}
                        item={item}
                        onToggle={handleToggle}
                        onOpenDetails={handleOpenDetails}
                        onDelete={handleDelete}
                        showCategory={showCategory}
                      />
                    ))}
                  </List>
                </Collapse>
              </Box>
            ))}
          </div>

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            className={styles.resetBtn}
          >
            Reset Checklist
          </Button>
        </Box>
      </Box>

      <ChecklistItemModal
        open={modalOpen}
        item={selectedItem}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        onToggle={handleToggle}
      />
    </Container>
  );
}

export default memo(Checklist);
